/**
 * Parses a candidate request timeframe into a whole number of days (FEAT-576).
 *
 * The product replaces the fixed weeks dropdown with a day count from 1 to 60, for both a
 * self-schedule request and a Candidate Availability request. Two boundaries feed that day count,
 * so this file gives one parser for each:
 *
 * 1. `parseTimeframeDays` reads the number that the counter input emits.
 * 2. `parseTimeframeDaysFromSeconds` reads a saved `Duration` from GraphQL.
 *
 * Both return the same branded `TimeframeDays`, so no caller repeats the range check. The shared
 * `CounterNumberInput` renders the day count in the settings dialog and in both create flows.
 */

import { err, ok, type Result } from 'neverthrow';

import { SECONDS_IN_A_DAY } from 'src/constants';

/** Smallest timeframe the product accepts. The settings whitelist uses the same bound. */
export const MIN_TIMEFRAME_DAYS = 1;

/** Largest timeframe the product accepts. It keeps the previous "2 months" option reachable. */
export const MAX_TIMEFRAME_DAYS = 60;

/**
 * The day count the counter starts on when no saved value is in range.
 *
 * The counter always shows a number, so each caller needs one. Two weeks matches the default of
 * the weeks picker that the counter replaces.
 */
export const DEFAULT_TIMEFRAME_DAYS = 14;

/**
 * Keeps `TimeframeDays` apart from a plain `number` in the type system.
 *
 * The symbol has no value at run time. It exists so that only the two parsers in this file can
 * make a `TimeframeDays`, and an unchecked number cannot pass in its place.
 */
declare const timeframeDaysBrand: unique symbol;

/** A whole number of days from `MIN_TIMEFRAME_DAYS` to `MAX_TIMEFRAME_DAYS`. */
export type TimeframeDays = number & { readonly [timeframeDaysBrand]: true };

/** Says why a value is not a timeframe in days. Each caller drops the value and keeps the last one. */
export type InvalidTimeframeDays =
  | { type: 'TIMEFRAME_DAYS_NOT_A_WHOLE_NUMBER' }
  | { type: 'TIMEFRAME_DAYS_OUT_OF_RANGE' };

/** Parses a day count. It keeps the range invariant in one place for both boundaries. */
export const parseTimeframeDays = (days: number): Result<TimeframeDays, InvalidTimeframeDays> => {
  if (!Number.isInteger(days)) {
    return err({ type: 'TIMEFRAME_DAYS_NOT_A_WHOLE_NUMBER' });
  }
  if (days < MIN_TIMEFRAME_DAYS || days > MAX_TIMEFRAME_DAYS) {
    return err({ type: 'TIMEFRAME_DAYS_OUT_OF_RANGE' });
  }
  return ok(days as TimeframeDays);
};

/** Parses a saved timeframe duration. An absent or partial-day duration is a failure. */
export const parseTimeframeDaysFromSeconds = (
  seconds: number | null | undefined
): Result<TimeframeDays, InvalidTimeframeDays> => parseTimeframeDays((seconds ?? 0) / SECONDS_IN_A_DAY);
